import type { ActiveTimer } from '../../types';
import { effectiveProgress } from '../timer/progressModel';
import { timerClockStart } from '../timer/timerModel';
import type { TaskProgressValue } from '../timer/TaskProgressField';

/**
 * P-10e (D-85) — the break / clock-out window over the running timers. Pure;
 * unit-tested in `src/utils/__tests__/endOfShiftModel.test.ts`.
 *
 *   יוצאת להפסקה — יש 2 טיימרים פעילים
 *   ☑ עיצוב דף הבית   00:42   מה עשיתי [   ]  סטטוס ⚪🔵🟣🟠🟢  התקדמות [━━●───]
 *   ☑ חיבור טופס      00:10   מה עשיתי [   ]  …
 *                [עצירת המסומנים ויציאה להפסקה]  [להשאיר פעילים]
 *
 * Every row starts CHECKED. "מה עשיתי" is optional (a stop, not a finish —
 * D-64). At a break, only the checked timers stop; "להשאיר פעילים" stops none.
 * At a clock-out the window is part of "סגירת יום" (D-84): the checked
 * timers stop with their notes, and an UNCHECKED timer is stopped anyway by
 * the clock-out itself, without notes (D-66 — no timer without attendance),
 * which the window says in a warning.
 */

export interface TimerCloseRow {
  timerId: string;
  ticketId: string;
  title: string;
  startedAt: string;
  running: boolean;
  /** Tasks get the status circles and the bar; a service ticket does not (D-47). */
  isTask: boolean;
  checked: boolean;
  summary: string;
  /** The task's status / bar when the window opened, and now. */
  before: TaskProgressValue;
  value: TaskProgressValue;
}

/** One row per timer of the employee, checked. */
export function closeRowsFromTimers(timers: readonly ActiveTimer[]): TimerCloseRow[] {
  return timers.map((tm) => {
    const before: TaskProgressValue = {
      status: tm.ticket_status ?? 'planning',
      progress: effectiveProgress(tm.ticket_status, tm.ticket_progress),
    };
    return {
      timerId: tm.id,
      ticketId: tm.ticket_id,
      title: tm.ticket_title || (tm.ticket_number ? `#${tm.ticket_number}` : ''),
      startedAt: timerClockStart(tm),
      running: tm.status === 'running',
      isTask: tm.task_type === 'task' && !!tm.ticket_status,
      checked: true,
      summary: '',
      before,
      value: before,
    };
  });
}

/** The timers to stop: the checked ones, each with its (optional) note. */
export function checkedStops(rows: readonly TimerCloseRow[]): Array<{ timer_id: string; summary?: string }> {
  return rows
    .filter((r) => r.checked)
    .map((r) => (r.summary.trim() ? { timer_id: r.timerId, summary: r.summary.trim() } : { timer_id: r.timerId }));
}

/** At a clock-out: are there timers that will be stopped without the employee's notes? */
export function hasUncheckedTimers(rows: readonly TimerCloseRow[]): boolean {
  return rows.some((r) => !r.checked);
}

/**
 * "סגירת יום" opens when there are suggestions, a documentation gap of 15+
 * minutes (`gapOpens`, the existing rule) — or now any running timer: the
 * clock-out never stops a timer without asking first.
 */
export function shouldOpenEndOfDay(input: { timers: number; gapOpens: boolean }): boolean {
  return input.timers > 0 || input.gapOpens;
}

export type CloseDayPrimary = 'stopConfirmAndLeave' | 'stopAndLeave' | 'confirmAndLeave' | 'logTime';

/**
 * The primary button of "סגירת יום": with timers it also stops the checked
 * ones ("עצירה, אישור ויציאה" / "עצירה ויציאה"); without timers it is as it
 * was ("אישור ויציאה" with suggestions, "רישום שעות" with only a gap).
 */
export function closeDayPrimary(input: { timers: number; suggestions: number }): CloseDayPrimary {
  if (input.timers > 0) return input.suggestions > 0 ? 'stopConfirmAndLeave' : 'stopAndLeave';
  return input.suggestions > 0 ? 'confirmAndLeave' : 'logTime';
}

/** "00:42" — the time a timer has been running (hours:minutes). */
export function elapsedHm(startedAt: string, now: number): string {
  const diff = Math.max(0, now - new Date(startedAt).getTime());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
